import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, AlertTriangle, RefreshCw, ServerCrash, WalletCards } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh, { ADMIN_AUTO_REFRESH_MS } from "../../lib/useAdminAutoRefresh";

export default function AdminGameCredits() {
  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [showSetupNeeded, setShowSetupNeeded] = useState(false);

  const saveRows = useCallback((nextRows) => {
    rowsRef.current = nextRows;
    setRows(nextRows);
  }, []);

  const loadCredits = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setSyncing(true);
    else setLoading(true);
    try {
      const currentRows = rowsRef.current;
      const liveGameNames = quiet
        ? currentRows.filter((row) => row.adapterReady !== false).map(gameIdFromAutomationRow).filter(Boolean)
        : [];
      const { data } = liveGameNames.length
        ? await api.post("/admin/automation/game-credits", { gameNames: liveGameNames })
        : await api.get("/admin/automation/game-credits");
      const nextRows = toRows(data);
      if (liveGameNames.length) {
        const refreshedIds = new Set(liveGameNames);
        const returnedIds = new Set(nextRows.map(gameIdFromAutomationRow).filter(Boolean));
        const byId = new Map(currentRows
          .filter((row) => {
            const rowId = gameIdFromAutomationRow(row);
            return !refreshedIds.has(rowId) || returnedIds.has(rowId);
          })
          .map((row) => [gameIdFromAutomationRow(row), row]));
        nextRows.forEach((row) => byId.set(gameIdFromAutomationRow(row), row));
        saveRows(Array.from(byId.values()));
      } else {
        saveRows(nextRows);
      }
      setError("");
      setLastUpdated(new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [saveRows]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  useAdminAutoRefresh(() => loadCredits({ quiet: true }));

  const visibleRows = useMemo(() => rows.filter((row) => shouldShowGameCreditRow(row)), [rows]);
  const liveRows = useMemo(() => visibleRows.filter((row) => row.ok !== false), [visibleRows]);
  const setupRows = useMemo(() => visibleRows.filter((row) => row.ok === false && !isProviderOutage(row)), [visibleRows]);

  const stats = useMemo(() => {
    const live = liveRows.length;
    const attention = visibleRows.filter((row) => row.ok === false).length;
    const total = liveRows.reduce((sum, row) => {
      const value = Number(row.backend_balance);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    return { live, attention, total };
  }, [liveRows, visibleRows]);

  const hiddenCount = rows.length - visibleRows.length;

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Game Credits</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Live provider balances refresh automatically. Temporary provider outages retry automatically; login problems can be fixed from Backend Attachments.
          </p>
        </div>
        <div className="rounded-full border border-emerald-700/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300">
          <span className="inline-flex items-center gap-2">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing || loading ? "animate-spin" : ""}`} />
            {syncing || loading ? "Syncing now" : `Live sync every ${ADMIN_AUTO_REFRESH_MS / 1000}s`}
          </span>
        </div>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-4">
        <Stat icon={WalletCards} label="Total backend credit" value={formatMoney(stats.total)} />
        <Stat icon={Activity} label="Live games" value={stats.live} />
        <Stat icon={ServerCrash} label="Needs attention" value={stats.attention} />
        <Stat icon={RefreshCw} label="Last sync" value={lastUpdated || "--"} />
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-700/30 bg-red-950/40 p-4 text-sm font-semibold text-red-200">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-white/10 bg-[#121214] p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <WalletCards className="h-5 w-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold">Live provider balances</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Live provider balances are shown here. Paused logins stay compact below.
            </p>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-48 animate-pulse rounded-xl border border-white/10 bg-white/[0.03]" />
            ))}
          </div>
        ) : visibleRows.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleRows.map((row) => (
              <GameCreditCard key={gameIdFromAutomationRow(row)} row={row} onRetry={() => loadCredits({ quiet: true })} syncing={syncing} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-white/5 bg-black/20 p-8 text-center text-sm text-slate-500">
            Attach provider backends first, then their balances will show here.
          </div>
        )}

        {setupRows.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-amber-100">
                    {setupRows.length} provider login{setupRows.length === 1 ? "" : "s"} need attention
                  </div>
                  <div className="text-xs text-amber-100/60">
                    Wrong passwords or locked provider logins are paused until you re-attach them.
                  </div>
                  {!showSetupNeeded && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {setupRows.slice(0, 4).map((row) => (
                        <span
                          key={gameIdFromAutomationRow(row)}
                          className="rounded-full border border-amber-400/20 bg-black/20 px-2 py-1 text-[11px] text-amber-100/80"
                        >
                          {row.displayName || row.name || gameIdFromAutomationRow(row)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSetupNeeded((value) => !value)}
                  className="rounded-md border border-amber-400/30 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-400/10"
                >
                  {showSetupNeeded ? "Hide" : "Show"}
                </button>
                <a
                  href="/admin/automation"
                  className="rounded-md bg-amber-300 px-3 py-2 text-xs font-bold text-black hover:bg-amber-200"
                >
                  Backend Attachments
                </a>
              </div>
            </div>
            {showSetupNeeded && (
              <div className="mt-4 grid gap-2 md:grid-cols-2">
                {setupRows.map((row) => (
                  <SetupNeededRow key={gameIdFromAutomationRow(row)} row={row} />
                ))}
              </div>
            )}
          </div>
        )}

        {hiddenCount > 0 && (
          <div className="mt-4 rounded-lg border border-white/5 bg-black/20 px-4 py-3 text-xs text-slate-500">
            {hiddenCount} unavailable game{hiddenCount === 1 ? "" : "s"} hidden because no supported backend attachment is active yet.
          </div>
        )}
      </section>
    </div>
  );
}

function GameCreditCard({ row, onRetry, syncing }) {
  const gameId = gameIdFromAutomationRow(row);
  const ok = row.ok !== false;
  const hasBalance = row.backend_balance !== null && row.backend_balance !== undefined && row.backend_balance !== "";
  const balanceUnavailable = ok && !hasBalance && Boolean(
    row.balance_unavailable || row.permission_limited || row.warning || row.raw?.balance_unavailable || row.raw?.permission_limited
  );
  const needsAttention = !ok;
  const outage = !ok && isProviderOutage(row);
  const status = outage ? "Provider unavailable" : needsAttention ? "Needs login" : hasBalance ? "Backend balance" : balanceUnavailable ? "Automation ready" : "Balance unavailable";
  const value = needsAttention ? "--" : hasBalance ? formatMoney(row.backend_balance) : balanceUnavailable ? "Active" : "--";
  const statusClass = needsAttention
    ? "border-amber-400/30 text-amber-200"
    : "border-emerald-700/30 text-emerald-300";

  return (
    <article className={`min-w-0 rounded-xl border bg-black/20 p-5 transition-colors ${
      needsAttention ? "border-amber-400/25 hover:border-amber-300/50" : "border-white/10 hover:border-[#D4AF37]/30"
    }`}>
      <div className="mb-4 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-slate-100">{row.displayName || row.name || gameId}</h3>
          <p className="mt-1 truncate text-xs uppercase tracking-wider text-slate-500">{gameId}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${statusClass}`}>
          {outage ? "offline" : needsAttention ? "paused" : "live"}
        </span>
      </div>
      <div className={`text-3xl font-bold tracking-tight tabular-nums ${
        needsAttention ? "text-amber-100" : balanceUnavailable ? "text-emerald-300" : "text-white"
      }`}>
        {value}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {status}
      </div>
      <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${
        needsAttention
          ? "border-amber-400/20 bg-amber-500/10 text-amber-100/80"
          : balanceUnavailable
          ? "border-emerald-500/15 bg-emerald-500/10 text-emerald-100/75"
          : "border-white/5 bg-white/[0.03] text-slate-500"
      }`}>
        {needsAttention
          ? shortError(row.error) || "Provider login needs attention. Re-attach this backend before live balance updates continue."
          : balanceUnavailable
          ? "Load, redeem, account create, and password reset are active. This provider login does not allow agent-balance viewing."
          : "Updates automatically while live."}
      </div>
      {outage ? (
        <button type="button" onClick={onRetry} disabled={syncing}
          className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-[#D4AF37]/30 px-3 py-2 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50">
          {syncing ? "Checking provider..." : "Retry balance"}
        </button>
      ) : <a
        href={`/admin/automation?gameId=${encodeURIComponent(gameId)}`}
        className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-[#D4AF37]/30 px-3 py-2 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/10"
      >
        {needsAttention ? "Fix login" : "Change account"}
      </a>}
    </article>
  );
}

function isProviderOutage(row) {
  return row?.error?.error_kind === "provider_unavailable" || row?.error?.detail?.error_kind === "provider_unavailable";
}

function SetupNeededRow({ row }) {
  const gameId = gameIdFromAutomationRow(row);
  return (
    <div className="min-w-0 rounded-md border border-amber-400/15 bg-black/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">{row.displayName || row.name || gameId}</div>
          <div className="truncate text-[11px] uppercase tracking-wider text-amber-100/50">{gameId}</div>
        </div>
        <span className="shrink-0 rounded-full border border-amber-400/20 px-2 py-0.5 text-[11px] text-amber-200">
          paused
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-xs text-amber-100/70">{shortError(row.error)}</p>
      <a
        href={`/admin/automation?gameId=${encodeURIComponent(gameId)}`}
        className="mt-3 inline-flex rounded-md border border-amber-400/25 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-400/10"
      >
        Fix login
      </a>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4">
      <Icon className="mb-3 h-5 w-5 text-[#D4AF37]" />
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function toRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (value && typeof value === "object") return [value];
  return [];
}

function gameIdFromAutomationRow(row = {}) {
  return String(row.gameId || row.gameName || row.id || row.name || "").trim();
}

function shouldShowGameCreditRow(row = {}) {
  if (!gameIdFromAutomationRow(row)) {
    return false;
  }
  if (row.attached === false || row.isAttached === false || row.hasAttachment === false) {
    return false;
  }
  const error = shortError(row.error).toLowerCase();
  if (!row.ok && (error.includes("unsupported game") || error.includes("not attached") || error.includes("backend is not attached"))) {
    return false;
  }
  return true;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "--";
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  return `$${number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortError(value) {
  if (!value) return "Provider did not return a balance.";
  if (typeof value === "string") return compactProviderError(value);
  const nestedText = nestedErrorText(value);
  if (typeof value?.detail === "string") return compactProviderError(value.detail);
  if (typeof value?.error === "string") return compactProviderError(value.error);
  if (typeof value?.message === "string") {
    const providerMessage = value?.response?.message || value?.response?.error;
    const messages = [value.message, providerMessage, nestedText].filter(Boolean).map(compactProviderError);
    return dedupeTextParts(messages).join(": ");
  }
  if (nestedText) return compactProviderError(nestedText);
  try {
    return compactProviderError(JSON.stringify(value));
  } catch {
    return "Provider error";
  }
}

function compactProviderError(text = "") {
  let clean = String(text || "").replace(/\s+/g, " ").trim();
  clean = clean.replace(/:\s*null\b/gi, "");
  clean = clean.replace(/^(.+?) backend login needs attention:\s*\1 backend login failed:\s*/i, "$1: ");
  clean = clean.replace(/^(.+?) backend login needs attention:\s*/i, "$1: ");
  clean = clean.replace(/^(.+?) backend login failed:\s*/i, "$1: ");
  clean = clean.replace(/\bbackend login needs attention:\s*/gi, "");
  clean = clean.replace(/\bbackend login failed:\s*/gi, "");
  clean = clean.replace(/\bbackend credentials are missing or inactive\.\s*/gi, "credentials missing or inactive. ");
  clean = clean.replace(/\s+:\s+/g, ": ");
  return clean.trim();
}

function dedupeTextParts(parts = []) {
  const result = [];
  parts.forEach((part) => {
    const clean = compactProviderError(part);
    if (!clean) return;
    const lower = clean.toLowerCase();
    if (result.some((existing) => existing.toLowerCase() === lower || existing.toLowerCase().includes(lower) || lower.includes(existing.toLowerCase()))) {
      return;
    }
    result.push(clean);
  });
  return result.slice(0, 3);
}

function nestedErrorText(value, seen = new WeakSet()) {
  if (!value || typeof value !== "object") return "";
  if (seen.has(value)) return "";
  seen.add(value);
  const parts = [];
  ["message", "msg", "error", "detail"].forEach((key) => {
    if (typeof value[key] === "string" && value[key].trim()) {
      parts.push(value[key].trim());
    }
  });
  ["response", "body", "data", "result"].forEach((key) => {
    const text = nestedErrorText(value[key], seen);
    if (text) parts.push(text);
  });
  return [...new Set(parts)].slice(0, 4).join(": ");
}
